import type { App } from '@hangar/shared';
import { toast } from 'sonner';
import { useAppMutations, useEnvVars, useProfiles } from '@/api/hooks';
import { showMutationError } from '@/lib/mutationError';
import { useCan } from '@/lib/permissions';
import { ConfirmArchiveDialog, Counted, type ImpactItem } from '@/components/ConfirmArchiveDialog';

interface Props {
  app: App | null;
  onOpenChange: (open: boolean) => void;
  onArchived?: (app: App) => void;
}

interface Counts {
  profiles: number | undefined;
  vars: number | undefined;
  canReadEnv: boolean;
}

/** What archiving the app stops and what stays; the env var line only for who may read them. */
function appArchiveImpact(app: App | null, { profiles, vars, canReadEnv }: Counts): ImpactItem[] {
  const envLine = [{ text: <><Counted n={vars} one="app-level environment variable stays" many="app-level environment variables stay" /> stored, unused.</> }];
  return [
    {
      text: (
        <>
          New builds are refused: <code className="font-mono text-xs text-foreground">hangar build --app {app?.slug}</code> stops working. Builds already started finish.
        </>
      ),
    },
    { text: <><Counted n={profiles} one="build profile goes" many="build profiles go" /> with it.</> },
    ...(canReadEnv ? envLine : []),
    { text: 'Build history, artifacts and env snapshots stay; the slug stays reserved in the project.' },
  ];
}

/** Archives the app (`PATCH /apps/{id}` with `archived: true`) and says what stops. */
function useArchiveApp(app: App | null, onOpenChange: (open: boolean) => void, onArchived?: (app: App) => void) {
  const { update } = useAppMutations();
  const confirm = async () => {
    if (!app) return;
    try {
      const archived = await update.mutateAsync({ appId: app.id, body: { archived: true }, etag: app.etag });
      toast.success(`${app.name} archived`, { description: `hangar build --app ${app.slug} is refused from now on.` });
      onOpenChange(false);
      onArchived?.(archived);
    } catch (e) {
      showMutationError(e, 'Could not archive the app');
    }
  };
  return { busy: update.isPending, confirm };
}

/** Archive an app (`PATCH /apps/{id}` with `archived: true`) after counting its profiles and env vars and saying what stops. */
export function ArchiveAppDialog({ app, onOpenChange, onArchived }: Props) {
  const { busy, confirm } = useArchiveApp(app, onOpenChange, onArchived);
  const canReadEnv = useCan('env:read', app?.projectId);
  const profiles = useProfiles(app?.id);
  const vars = useEnvVars({ appId: app?.id }, Boolean(app) && canReadEnv.allowed);
  return (
    <ConfirmArchiveDialog
      open={Boolean(app)}
      onOpenChange={onOpenChange}
      testId="app-archive-dialog"
      title={`Archive ${app?.name ?? 'the app'}?`}
      description="It leaves its project and every list; nothing is deleted."
      impact={appArchiveImpact(app, { profiles: profiles.data?.length, vars: vars.data?.length, canReadEnv: canReadEnv.allowed })}
      confirmLabel="Archive app"
      busy={busy}
      onConfirm={() => void confirm()}
    />
  );
}
